const DEFAULT_WIDGETS_SCRIPT = 'https://donorbox.org/widgets.js';
const WIDGET_TAG = 'dbox-widget';

export const parseDonorboxEmbedCode = (embedCode) => {
  if (!embedCode?.trim() || typeof DOMParser === 'undefined') return null;

  const doc = new DOMParser().parseFromString(embedCode.trim(), 'text/html');
  const script = doc.querySelector('script[src*="donorbox.org"]');
  const widget = doc.querySelector(WIDGET_TAG);

  if (!widget) return null;

  return {
    scriptSrc: script?.getAttribute('src') || DEFAULT_WIDGETS_SCRIPT,
    scriptType: script?.getAttribute('type') || 'module',
    widgetHtml: widget.outerHTML,
  };
};

let widgetsScriptPromise = null;

const ensureWidgetsScript = (scriptSrc, scriptType) => {
  if (typeof document === 'undefined') return Promise.resolve();

  if (customElements.get(WIDGET_TAG) && document.querySelector(`script[src="${scriptSrc}"]`)) {
    return Promise.resolve();
  }

  if (!document.querySelector(`script[src="${scriptSrc}"]`)) {
    if (!widgetsScriptPromise) {
      widgetsScriptPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.type = scriptType;
        script.src = scriptSrc;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = reject;
        document.body.appendChild(script);
      }).catch((error) => {
        widgetsScriptPromise = null;
        throw error;
      });
    }
    return widgetsScriptPromise;
  }

  return Promise.resolve();
};

const waitForDboxWidget = () => {
  if (typeof customElements === 'undefined') return Promise.resolve();
  if (customElements.get(WIDGET_TAG)) return Promise.resolve();
  return customElements.whenDefined(WIDGET_TAG);
};

const nextFrame = () =>
  new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(resolve));
  });

const mountDboxWidget = (container, widgetHtml) => {
  if (container.querySelector(WIDGET_TAG)) return container.querySelector(WIDGET_TAG);
  container.innerHTML = widgetHtml;
  return container.querySelector(WIDGET_TAG);
};

export const loadDonorboxOmniGiveEmbed = (container, embedCode) => {
  if (!container || !embedCode) return () => {};

  const parsed = parseDonorboxEmbedCode(embedCode);
  if (!parsed?.widgetHtml) return () => {};

  let cancelled = false;
  container.innerHTML = '';

  ensureWidgetsScript(parsed.scriptSrc, parsed.scriptType)
    .then(() => waitForDboxWidget())
    .then(() => nextFrame())
    .then(() => {
      if (cancelled || !container.isConnected) return;
      mountDboxWidget(container, parsed.widgetHtml);
      scheduleDonorboxResize(container);
    })
    .catch(() => {});

  return () => {
    cancelled = true;
    container.innerHTML = '';
  };
};

export const resizeDonorboxWidget = (container) => {
  if (typeof window === 'undefined' || !window.donorbox?.resizeDonationWidget) return;

  const iframe = container?.querySelector?.('iframe');
  if (iframe) {
    window.donorbox.resizeDonationWidget(iframe);
    return;
  }

  const widget = container?.querySelector?.(WIDGET_TAG);
  if (widget) {
    window.donorbox.resizeDonationWidget(widget);
  }
};

export const scheduleDonorboxResize = (container) => {
  if (!container) return;

  const attempt = () => resizeDonorboxWidget(container);
  attempt();
  [100, 300, 600, 1200].forEach((delay) => {
    setTimeout(attempt, delay);
  });
};

export const isOmniGiveEmbedCode = (embedCode) =>
  Boolean(embedCode && String(embedCode).includes(WIDGET_TAG));
